'use client';

/**
 * Appointment - Mode dispatcher for booking forms
 *
 * This component dispatches to the appropriate booking form based on mode:
 * - 'minimal': QuickLeadsForm (simple lead capture)
 * - 'quicklead': ManualBookingForm (static availability, team follows up)
 * - 'booking': MusiCalForm (real-time Google Calendar availability)
 */

import dynamic from 'next/dynamic';
import { Instrument } from '@/app/instruments';
import QuickLeadsForm from './QuickLeadsForm';

// Kept for a future booking-first flow, but no caller passes `mode` today, so
// neither form is reachable. Loading them lazily keeps the code without
// shipping it to every visitor who only ever sees QuickLeadsForm.
const ManualBookingForm = dynamic(() => import('./booking/ManualBookingForm'));
const MusiCalForm = dynamic(() => import('./booking/MusiCalForm'));

export type AppointmentMode = 'minimal' | 'booking' | 'quicklead';

export interface AppointmentProps {
  onClose: () => void;
  autoFocus?: boolean | undefined;
  instrument?: Instrument;
  /**
   * Form mode:
   * - 'minimal': Shows QuickLeadsForm (default, current behavior)
   * - 'booking': MusiCalForm - Full booking with dynamic Google Calendar availability
   * - 'quicklead': ManualBookingForm - Full booking UI with static availability, team follows up
   */
  mode?: AppointmentMode;
}

export default function Appointment({
  onClose,
  autoFocus,
  instrument,
  mode = 'minimal',
}: AppointmentProps) {
  switch (mode) {
    case 'minimal':
      return (
        <QuickLeadsForm
          onClose={onClose}
          autoFocus={autoFocus}
          instrument={instrument}
        />
      );

    case 'quicklead':
      return (
        <ManualBookingForm
          onClose={onClose}
          autoFocus={autoFocus}
          instrument={instrument}
        />
      );

    case 'booking':
      return (
        <MusiCalForm
          onClose={onClose}
          autoFocus={autoFocus}
          instrument={instrument}
        />
      );
  }
}
